import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { Button } from '../../../shared/ui/button'
import { Input } from '../../../shared/ui/input'
import { BodyText, HeadingText } from '../../../shared/ui/typography'
import { UserBox } from '../../../shared/assets/svg'
import { Danger } from 'iconsax-reactjs'
import { useMutation } from '@tanstack/react-query'
import { loginService } from '../service'
import { LoginDataType } from '../types'
import { toast } from 'sonner'
import { useDispatch } from 'react-redux'
import { PAGES } from '../../../shared/lib/pageUrlConstants'
import { useGeolocation } from '../../../features/account-settings/hooks/use-geolocation'
import { login } from '../../../redux/slices/authSlice'

const Login = () => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")

  const { userLocation, locationStatus } = useGeolocation();

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { mutateAsync, isError, isPending, } = useMutation({ mutationFn: (data: LoginDataType) => loginService(data) })

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (locationStatus === 'denied') {
      toast.error("Please allow location permission to continue")
      return
    }

    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission() // fire-and-forget, does not block login
    }
    const data: LoginDataType = {
      email,
      password,
      latitude: userLocation?.latitude,
      longitude: userLocation?.longitude
    }
    try {
      const res = await mutateAsync(data)
      toast.success(res?.message)
      localStorage.setItem("token", res?.data?.token);
      if (res?.data?.mfaSetupRequired) {
        return navigate(PAGES.setup2fa)
      }
      if (res?.data?.mfaRequired) {
        navigate(PAGES.verifyMfa)
      } else {
        dispatch(login(res?.data));
        window.location.replace(PAGES.dashboard);
      }
    } catch (error: any) {
      setErrorMsg(error?.response?.data?.message || error?.message || "Something went wrong")
      toast.error(error?.response?.data?.message || error?.message || "Something went wrong", { position: "top-right" })
    }
  }


  return (
    <div className="bg-white md:max-w-[628px]  mx-auto w-11/12 rounded-[32px] p-4 md:p-[32px] shadow-[0px_4px_40px_rgba(0,0,0,0.02)] border border-primary-80">
      <div className="flex flex-col items-start gap-6">
        <div className="relative h-12 p-0">
          <UserBox className="absolute -top-2 md:-top-5 -left-5" />
        </div>

        <div className="space-y-1">
          <HeadingText className="text-2xl font-semibold leading-tight md:text-3xl">
            Welcome back
          </HeadingText>
          <BodyText className="text-gray-80 text-sm font-[350]">Sign in to Tradevu Admin</BodyText>
        </div>

        <form className="w-full mt-2 space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Input
              type="email"
              placeholder="Email"
              name="email"
              value={email}
              required
              onChange={(e) => setEmail(e.target.value)}
              className="newInput"
            />
          </div>

          <div className="space-y-2">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              name="password"
              value={password}
              required
              onChange={(e) => setPassword(e.target.value)}
              className="h-[56px] px-4 placeholder:text-gray-70 rounded-[8px] bg-primary-80 border-none focus-visible:ring-1 focus-visible:ring-primary-30"
              endIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-gray-20 hover:text-gray-30"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              }
            />
            {isError && errorMsg && (
              <p className="text-error-30 flex items-center gap-1 font-[350] text-xs">
                <Danger size={20} /> <span>{errorMsg}</span>
              </p>
            )}
          </div>

          <div className="flex items-center justify-end gap-5 pt-2">
            <a
              href="mailto:support@tradevu.co"
              className="text-sm font-[450] text-gray-30 hover:text-primary-30 transition-colors"
              target="_blank"
              rel="noreferrer"
            >
              Forgot password?
            </a>
            <Button
              type="submit"
              size="lg"
              disabled={isPending || !email || !password}
              className="!h-[40px] rounded-[8px] text-base font-[450] bg-[#161827] hover:bg-[#20243a]"
            >
              {isPending ? "Loading..." : "Sign In"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login
